import * as Sentry from "@sentry/browser";
import type { Annotations, Layout } from "plotly.js";
import type { JsonDataWithFields } from "./bindings/json_line_data.js";
import type { JsonValue } from "./bindings/serde_json/JsonValue.js";
import { config, tableMetadata } from "./constants.js";
import { populateTable } from "./createTable.js";
import { dataFetcher } from "./dataFetching.js";
import { registryMetadata } from "./metadata.js";
import { type FieldAxisMap, layoutForConfig } from "./plotConfig.js";
import { organisePlotData } from "./plotDataOrganizer.js";
import { formatBucket, type ThinningInfo, thinTableData } from "./thinning.js";
import {
	loadResolvedUserConfig,
	type TableUserConfig,
} from "./userPlotConfig.js";
import {
	setXAxisRange,
	updateButtons,
	updateQueryString,
	// getDateISOString
} from "./utils.js";

// Plotly supports `name` on annotations, but @types/plotly.js omits it
type NamedAnnotation = Partial<Annotations> & { name?: string };

export class GeneratePlot {
	plotElement?: Plotly.PlotlyHTMLElement;
	loadingElement: HTMLDivElement;
	overlay: HTMLElement | null;
	pageTitle: HTMLElement | null;
	dataFetcher: dataFetcher;
	default_from_date: string;
	todayDateTime?: Date;
	today?: string;
	updatedEvent: CustomEvent<unknown>;
	loadingTimer: NodeJS.Timeout | null = null;
	plotData: {
		[key: string]: Plotly.Data[];
	};
	intervalId: NodeJS.Timeout | null = null;
	fetchPeriod: number = 600000; // 600000 ms = 10 minutes
	lastAutoToDate: string | null = null; // to_date written by the last periodic refresh
	tableId: number | undefined; // Current table ID from data, set when data is fetched
	currentLayout: Partial<Layout>;
	fieldAxisMap: FieldAxisMap = new Map();
	userConfig: TableUserConfig | null = null; // field choices from the field selection panel
	configStationId: string | null = null; // station the current layout/config was built for
	suppressNextUrlUpdate: boolean = false;
	thinning: ThinningInfo | null = null; // set when the plotted data is min/max thinned
	constructor(
		public divId: string,
		public activeStation: string,
	) {
		this.loadingElement = document.createElement("div");
		this.loadingElement.id = "loadingDiv";
		this.loadingElement.innerHTML = '<div class="spinner"></div>';
		this.updatedEvent = new CustomEvent("plotUpdated");
		this.plotData = {};
		const lastWeek = new Date();
		lastWeek.setDate(lastWeek.getDate() - 7);
		this.default_from_date = lastWeek.toISOString();

		this.overlay = document.getElementById("overlay");
		this.pageTitle = document.getElementById("pageTitle");
		this.dataFetcher = new dataFetcher();
		// Start with minimal layout - will be set from data in reactPlot()
		this.currentLayout = {};
		if (!this.pageTitle) console.error("No page Title div found.");
		if (activeStation && this.pageTitle) {
			const station = stationMap[this.activeStation];
			if (station?.name) {
				this.pageTitle.innerText = station.name;
			} else {
				console.error(
					`Station '${this.activeStation}' not found in stationMap.`,
				);
			}
		}

		this.initPlots();
		this.startPeriodicFetching();
	}
	initPlots(): void {
		this.showLoadingElement(); //always show on load
		this.todayDateTime = new Date();
		this.today = this.todayDateTime.toISOString();
		Plotly.newPlot(this.divId, [], this.currentLayout, config)
			.then((el) => this.initPlot(el))
			.catch((e) => console.error(e)); // maybe not very efficient to pass empty data?
		//set Range
		const urlParams = new URLSearchParams(window.location.search);
		// Suppress URL update only for the first call if no params
		this.suppressNextUrlUpdate = !(
			urlParams.has("from_date") ||
			urlParams.has("to_date") ||
			urlParams.has("station_id")
		);
		const from_date = urlParams.get("from_date") ?? this.default_from_date;
		const to_date = urlParams.get("to_date");
		setXAxisRange(this.divId, from_date, to_date);
		this.updatePlot(from_date, to_date).catch((e) => console.error(e));

		updateButtons(this.activeStation);
		window.addEventListener("resize", () => {
			this.setLayout();
		});
	}
	initPlot(el: Plotly.PlotlyHTMLElement): void {
		this.plotElement = el;
		this.plotElement.appendChild(this.loadingElement);
		this.plotElement.on("plotly_relayout", (eventData) => {
			// https://github.com/plotly/plotly.js/issues/1877
			if (eventData["xaxis.range[0]"] && eventData["xaxis.range[1]"]) {
				const endDate = new Date(eventData["xaxis.range[1]"]);
				const newEnd = endDate.toISOString();

				const startDate = new Date(eventData["xaxis.range[0]"]);
				const newStart = startDate.toISOString();
				this.updatePlot(newStart, newEnd).catch((e) => console.error(e));
			} else if (eventData["xaxis.range"]) {
				// @ts-expect-error see https://github.com/plotly/plotly.js/issues/1877
				const endDate = new Date(eventData["xaxis.range"][1]);
				const newEnd = endDate.toISOString();

				// @ts-expect-error see https://github.com/plotly/plotly.js/issues/1877
				const startDate = new Date(eventData["xaxis.range"][0]);
				const newStart = startDate.toISOString();

				this.updatePlot(newStart, newEnd).catch((e) => console.error(e));
			}
			if (eventData["xaxis.autorange"]) {
				// on double click, causes auto-range
				const xAxisRange = this.plotElement?.layout.xaxis.range;
				if (xAxisRange) {
					const startDate = new Date(xAxisRange[0]);
					const newStart = startDate.toISOString();

					const endDate = new Date(xAxisRange[1]);
					const newEnd = endDate.toISOString();
					updateQueryString(this.activeStation, newStart, newEnd);
					if (this.tableId !== undefined) {
						populateTable(
							this.divId,
							newStart,
							newEnd,
							this.tableId,
							this.thinning,
						);
					}
				}
			}
		});

		this.plotElement.addEventListener("plotUpdated", () => {
			this.hideLoadingElement();
		});
	}

	async updatePlot(
		from_date: string | null,
		to_date: string | null,
	): Promise<void> {
		//update query string only if not suppressed for this call
		const urlParams = new URLSearchParams(window.location.search);
		const startDate =
			from_date ?? urlParams.get("from_date") ?? this.default_from_date;
		const endDate = to_date ?? urlParams.get("to_date");

		await Sentry.startSpan(
			{
				op: "ui.update",
				name: "updatePlot",
				attributes: {
					station: this.activeStation,
					from_date: startDate,
					to_date: endDate ?? "(now)",
				},
			},
			async (span) => {
				if (!this.suppressNextUrlUpdate) {
					updateQueryString(this.activeStation, startDate, endDate);
				} else {
					this.suppressNextUrlUpdate = false;
				}
				//grab new data
				const newData = await this.dataFetcher.fetchData(
					this.activeStation,
					startDate,
					endDate,
				);
				if (!this.loadingTimer)
					this.loadingTimer = setTimeout(() => this.showLoadingElement(), 200); //only show if display is taking long
				this.reactPlot(newData, startDate, endDate);
				if (this.tableId !== undefined) {
					populateTable(
						this.divId,
						startDate,
						endDate,
						this.tableId,
						this.thinning,
					);
				}

				//update plotData and overlay
				if (!this.plotElement) {
					console.error(`Plot element is undefined:${this.plotElement}`);
					return;
				}
				this.plotData[this.activeStation] = this.plotElement.data;
				const tracesCount = this.plotElement.data.length;
				span.setAttribute("traces_count", tracesCount);
				Sentry.logger.info(
					Sentry.logger
						.fmt`updatePlot rendered ${tracesCount} traces for station ${this.activeStation}`,
					{ station: this.activeStation, traces_count: tracesCount },
				);
				if (tracesCount > 0) {
					this.hideOverlay();
				} else {
					this.showOverlay();
				}
				this.setLayout();
				// Emit custom event
				this.plotElement.dispatchEvent(this.updatedEvent);
			},
		);
	}

	/**
	 * Auto-refresh only makes sense for a view that ends at (or near) now: a past
	 * range can't gain new data, and refreshing it would stretch it to now.
	 */
	viewFollowsNow(): boolean {
		const toDate = new URLSearchParams(window.location.search).get("to_date");
		// Unchanged since the last auto-refresh (e.g. after the laptop slept): keep following
		if (!toDate || toDate === this.lastAutoToDate) return true;
		return new Date(toDate).getTime() >= Date.now() - 2 * this.fetchPeriod;
	}

	startPeriodicFetching(): void {
		if (!this.intervalId) {
			this.intervalId = setInterval(() => {
				if (!this.viewFollowsNow()) return;
				this.todayDateTime = new Date();
				this.today = this.todayDateTime.toISOString();

				const to_date = new Date().toISOString();
				this.lastAutoToDate = to_date;
				Sentry.logger.info(
					Sentry.logger
						.fmt`Periodic fetch triggered for station ${this.activeStation}`,
					{ station: this.activeStation, to_date },
				);
				this.updatePlot(null, to_date).catch((e) => console.error(e));
			}, this.fetchPeriod);
		}
	}

	stopPeriodicFetching(): void {
		if (this.intervalId) {
			clearInterval(this.intervalId);
			this.intervalId = null;
		}
	}

	setStation(id: string): void {
		if (id !== this.activeStation) {
			const plotData = this.plotData[id] ?? [];
			Plotly.react(this.divId, plotData, this.currentLayout, config).catch(
				(e) => console.error(e),
			);
			if (this.pageTitle) {
				const station = stationMap[id];
				if (station?.name) {
					this.pageTitle.innerText = station.name;
				} else {
					console.error(`Station '${id}' not found in stationMap.`);
				}
			}
			this.activeStation = id;
			updateButtons(this.activeStation);
		}
	}

	hideLoadingElement(): void {
		if (this.loadingTimer) clearTimeout(this.loadingTimer);
		this.loadingTimer = null;
		if (this.loadingElement.style.display !== "none") {
			this.loadingElement.style.opacity = "0";
			setTimeout(() => {
				this.loadingElement.style.display = "none";
			}, 500); // Match this duration with the transition duration
		}
	}
	showLoadingElement(): void {
		if (this.loadingElement.style.display !== "flex") {
			this.loadingElement.style.display = "flex";
			setTimeout(() => {
				this.loadingElement.style.opacity = "1";
			}, 10); // Small delay to ensure display change is applied
		}
	}
	setLayout(): void {
		const width = window.innerWidth;

		if (width <= 992) {
			this.currentLayout.legend = {
				x: 0.5,
				y: -0.3,
				xanchor: "center",
				yanchor: "top",
				orientation: "h",
				groupclick: "toggleitem",
			};
			this.currentLayout.font = { size: 9 };
		} else {
			this.currentLayout.legend = {
				x: 1.2,
				y: 1,
				groupclick: "toggleitem",
			};
			this.currentLayout.font = { size: 12 };
		}
		Plotly.relayout(this.divId, this.currentLayout).catch((e) =>
			console.error(e),
		);
	}

	reactPlot(
		newData: JsonDataWithFields,
		startDate: string,
		endDate: string | null,
	): void {
		if (this.activeStation in newData.data) {
			const stationData = newData.data[this.activeStation];
			const tableIds = Object.keys(stationData);

			if (tableIds.length > 0) {
				const tableIdStr = tableIds[0];
				const newTableId = Number.parseInt(tableIdStr, 10);
				if (
					newTableId !== this.tableId ||
					this.configStationId !== this.activeStation
				) {
					this.tableId = newTableId;
					// Rebuild the layout only when the table or station changes: saved field
					// choices are per station (falling back to a per-table default)
					this.userConfig = loadResolvedUserConfig(
						this.activeStation,
						this.tableId,
					);
					this.configStationId = this.activeStation;
					const layoutWithMap = layoutForConfig(this.tableId, this.userConfig);
					this.currentLayout = layoutWithMap.layout;
					this.fieldAxisMap = layoutWithMap.fieldAxisMap;
					this.setMinimumHeight(layoutWithMap.effectiveDomains);
				}
				// If tableId unchanged, this.currentLayout retains its state including zoom

				// Extract the specific table data
				const tableData = stationData[tableIdStr];

				const thinned = thinTableData(
					tableData,
					newData.fields[tableIdStr],
					registryMetadata[this.tableId]?.sampling_interval_seconds,
					undefined,
					{
						startMs: new Date(startDate).getTime(),
						endMs: endDate ? new Date(endDate).getTime() : Date.now(),
					},
				);
				this.thinning = thinned.info;
				const plotData = organisePlotData(
					thinned.tableData,
					this.tableId,
					this.fieldAxisMap,
					newData.fields,
					{
						// thinned rows are half a bucket apart
						samplingIntervalSeconds: thinned.info
							? thinned.info.bucketSeconds / 2
							: undefined,
						rowKinds: thinned.rowKinds,
						hiddenFields: this.hiddenFields(),
					},
				);
				this.setThinningAnnotation();
				Plotly.react(this.divId, plotData, this.currentLayout, config).catch(
					(e) => console.error(e),
				);
			} else {
				console.error(
					`No table ID found in data for station ${this.activeStation}`,
				);
			}
		}
	}

	/** Small label above the plot while the data is min/max thinned */
	setThinningAnnotation(): void {
		const others = (
			(this.currentLayout.annotations ?? []) as NamedAnnotation[]
		).filter((a) => a.name !== "thinning");
		const annotations: NamedAnnotation[] = this.thinning
			? [
					...others,
					{
						name: "thinning",
						text: `Min/max per ${formatBucket(this.thinning.bucketSeconds)}: zoom in to see every reading`,
						xref: "paper",
						yref: "paper",
						x: 0,
						y: 1,
						xanchor: "left",
						yanchor: "bottom",
						yshift: 8,
						showarrow: false,
						font: { size: 11, color: "#6c757d" },
					},
				]
			: others;
		this.currentLayout.annotations = annotations;
	}

	hiddenFields(): Set<string> {
		return new Set(
			Object.entries(this.userConfig ?? {})
				.filter(([, fc]) => !fc.visible)
				.map(([name]) => name),
		);
	}

	/**
	 * Apply field choices from the field selection panel: rebuild the layout and
	 * redraw the current range (served from the fetch cache).
	 */
	applyFieldConfig(config: TableUserConfig): void {
		if (this.tableId === undefined) return;
		this.userConfig = config;
		const layoutWithMap = layoutForConfig(this.tableId, config);
		this.currentLayout = layoutWithMap.layout;
		this.fieldAxisMap = layoutWithMap.fieldAxisMap;
		this.setMinimumHeight(layoutWithMap.effectiveDomains);

		const xRange = this.plotElement?.layout?.xaxis?.range;
		const from = xRange
			? new Date(xRange[0]).toISOString()
			: this.default_from_date;
		const to = xRange ? new Date(xRange[1]).toISOString() : null;
		this.updatePlot(from, to).catch((e) => console.error(e));
	}

	/** @param effectiveDomains rows in use, when a custom layout differs from the table's */
	setMinimumHeight(effectiveDomains?: number): void {
		if (!this.tableId) {
			console.error("No table ID set, cannot determine layout for height.");
			return;
		}
		const numRows =
			effectiveDomains ?? tableMetadata[this.tableId]?.numDomains;
		if (numRows === undefined) return;
		// Set minimum height: base height + pixels per row
		const baseHeight = 200;
		const pixelsPerRow = 150; // Space per domain row (adjust as needed)
		const minHeight = baseHeight + numRows * pixelsPerRow;

		const plotDiv = document.getElementById(this.divId);
		if (plotDiv) {
			plotDiv.style.minHeight = `${minHeight}px`;
			// Force Plotly to redraw with new container size
			Plotly.Plots.resize(plotDiv);
		}
	}

	showOverlay(): void {
		if (this.overlay) this.overlay.style.display = "flex";
	}

	hideOverlay(): void {
		if (this.overlay) this.overlay.style.display = "none";
	}
	getPointsinRange(
		data: {
			[x: string]: JsonValue[];
		},
		startDate: string,
		endDate: string | null,
	): number {
		const timestamps = data[0] as string[];
		const startTime = new Date(startDate).getTime();
		const endTime = endDate ? new Date(endDate).getTime() : Date.now();
		const indicesInRange = timestamps
			.map((ts, index) => ({ ts, index }))
			.filter(({ ts }) => {
				const t = new Date(ts).getTime();
				return t >= startTime && t <= endTime;
			})
			.map(({ index }) => index);

		const numVisiblePoints = indicesInRange.length;
		return numVisiblePoints;
	}
}
